/**
 * <prelaunch-omnisend> — fires an Omnisend custom event after a successful
 * waitlist signup, so custom automations can trigger on it.
 *
 * Uses the web-tracking snippet injected by the Omnisend Shopify app
 * (window.omnisend queue) — the sanctioned client-side API. The REST API is
 * deliberately NOT called from the browser: it needs the account's secret
 * X-API-KEY, which must never ship in theme code.
 *
 * Flow: the native Shopify form posts and the page reloads, so field values
 * are stashed in sessionStorage at submit time; when the reloaded page
 * contains the success message ([data-omnisend-success]), the stash is
 * flushed to Omnisend as identifyContact + a custom event, then cleared.
 * The event name comes from the section setting via data-event-name.
 */
class PrelaunchOmnisend extends HTMLElement {
  connectedCallback() {
    this.storageKey = 'prelaunch-omnisend-signup';
    this.form = this.querySelector('form');
    this.form?.addEventListener('submit', () => this.stash());

    if (this.querySelector('[data-omnisend-success]')) this.flush();
  }

  stash() {
    const field = (name) => this.form.querySelector(`input[name="contact[${name}]"]`)?.value.trim() || '';
    const data = {
      email: field('email'),
      firstName: field('first_name'),
      phone: field('phone'),
    };
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify(data));
    } catch {
      // Storage unavailable (private mode etc.) — signup still works,
      // only the custom event is skipped.
    }
  }

  flush() {
    let data;
    try {
      data = JSON.parse(sessionStorage.getItem(this.storageKey));
      sessionStorage.removeItem(this.storageKey);
    } catch {
      return;
    }
    if (!data?.email) return;

    const eventName = this.dataset.eventName || 'joined prelaunch waitlist';
    const contact = { email: data.email };
    if (data.phone) contact.phone = data.phone;
    if (data.firstName) contact.firstName = data.firstName;

    // The track event goes through the pre-load queue (processed when the
    // snippet initialises), so it's pushed exactly once, immediately.
    window.omnisend = window.omnisend || [];
    window.omnisend.push([
      'track',
      eventName,
      {
        origin: 'storefront',
        properties: {
          firstName: data.firstName,
          source: 'prelaunch-landing',
        },
      },
    ]);

    // identifyContact is documented as a direct method call, which only
    // exists once the snippet has loaded — retry briefly until it appears.
    // Repeat calls with the same payload are idempotent, so a retry racing
    // the queue is harmless.
    this.identifyWithRetry(contact, 10);
  }

  identifyWithRetry(contact, attemptsLeft) {
    if (typeof window.omnisend?.identifyContact === 'function') {
      window.omnisend.identifyContact(contact);
      return;
    }
    if (attemptsLeft > 0) {
      setTimeout(() => this.identifyWithRetry(contact, attemptsLeft - 1), 1000);
    }
  }
}

if (!customElements.get('prelaunch-omnisend')) {
  customElements.define('prelaunch-omnisend', PrelaunchOmnisend);
}
